import { useState, useEffect, useRef } from "react"
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Tabs,
    Tab,
    TextField,
    MenuItem,
    Paper,
    Checkbox,
    FormControlLabel,
    Grid,
    FormHelperText,
} from "@mui/material"
import Autocomplete from "@mui/material/Autocomplete"
import Chip from "@mui/material/Chip"
import CampaignIcon from "@mui/icons-material/Campaign"
import GroupIcon from "@mui/icons-material/Group"
import CloseIcon from "@mui/icons-material/Close"
import { LoadingButton } from "@mui/lab"
import api from "../../api/api"
import en_ja from "../../translations/en_ja"

const AddNotification = ({
    dialogOpen,
    onClose,
    editMode = false,
    onSubmit,
    setSnackbarType,
    setSnackbarOpen,
    setSnackbarMessage,
    notificationData,
}) => {
    const [tab, setTab] = useState(0)
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState({})
    const [emailOptions, setEmailOptions] = useState([])
    const [fetchingUsers, setFetchingUsers] = useState(false)
    const [estimatedReach, setEstimatedReach] = useState(0)
    const [showAll, setShowAll] = useState(false)
    const [isEditChange, setIsEditChange] = useState(false)

    // Store original data snapshot to compare against
    const originalDataRef = useRef(null)

    const defaultFormData = {
        title: "",
        message: "",
        type: "",
        target_audience: {
            allCustomers: false,
            gender: null,
            ageRange: { from: "", to: "" },
            customerType: null,
            selected_emails: [],
            bmiOver25: false,
        },
    }

    const [formData, setFormData] = useState(defaultFormData)

    const handleSelectAll = () => {
        handleAudienceChange("selected_emails", emailOptions)
        setShowAll(false)
    }

    useEffect(() => {
        if (editMode && notificationData) {
            const ta = notificationData.target_audience || {}
            const populated = {
                title: notificationData.title || "",
                message: notificationData.message || "",
                type: notificationData.type || "",
                target_audience: {
                    allCustomers: ta.allCustomers || false,
                    gender: ta.gender ?? null,
                    ageRange: {
                        from: ta.ageRange?.from ?? "",
                        to: ta.ageRange?.to ?? "",
                    },
                    customerType: ta.customerType || null,
                    selected_emails: ta.selected_emails || [],
                    bmiOver25: ta.bmiOver25 || false,
                },
            }
            setFormData(populated)

            originalDataRef.current = JSON.stringify(populated)
            setIsEditChange(false)

            setEstimatedReach((ta.selected_emails || []).length)
        } else {
            originalDataRef.current = null
            setIsEditChange(false)
        }
    }, [editMode, notificationData])

    useEffect(() => {
        if (editMode && originalDataRef.current) {
            const current = JSON.stringify(formData)
            setIsEditChange(current !== originalDataRef.current)
        }
    }, [formData, editMode])

    const handleTabChange = (_, newValue) => setTab(newValue)

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData((prev) => ({ ...prev, [name]: value }))
        setErrors((prev) => ({ ...prev, [name]: "" }))
    }

    const handleAudienceChange = (key, value) => {
        setFormData((prev) => ({
            ...prev,
            target_audience: {
                ...prev.target_audience,
                [key]: value,
            },
        }))
    }

    const validateForm = () => {
        const newErrors = {}
        if (!(formData.title || "").trim()) newErrors.title = en_ja["Title is required"]
        if (!(formData.message || "").trim()) newErrors.message = en_ja["Message is required"]
        if (!(formData.type || "").trim()) newErrors.type = en_ja["Type is required"]

        if (formData.target_audience.selected_emails.length === 0) {
            newErrors.selected_emails = en_ja["Please select at least one customer email"]
        }
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleClose = () => {
        setFormData(defaultFormData)
        setErrors({})
        setShowAll(false)
        setIsEditChange(false)
        setEstimatedReach(0)
        originalDataRef.current = null
        onClose()
    }

    useEffect(() => {
        const ta = formData.target_audience
        const ageFrom = ta.ageRange?.from ?? ""
        const ageTo = ta.ageRange?.to ?? ""

        if (
            !ta.allCustomers &&
            ta.gender === null &&
            !ageFrom &&
            !ageTo &&
            !ta.customerType &&
            !ta.bmiOver25
        ) {
            setEmailOptions([])
            setEstimatedReach(ta.selected_emails.length)
            return
        }

        let active = true
        const handler = setTimeout(async () => {
            setFetchingUsers(true)
            try {
                const payload = {
                    allCustomers: ta.allCustomers,
                    gender: ta.gender,
                    ageRange: ageFrom && ageTo ? ta.ageRange : null,
                    customerType: ta.customerType,
                    bmiOver25: ta.bmiOver25,
                }
                Object.keys(payload).forEach((k) => payload[k] == null && delete payload[k])

                const res = await api.post("/v1/admin/notifications/filtered-users", payload)
                const data = res?.data
                const items = data?.data || data?.users || data?.items || data || []
                const emails = items.map((u) => u?.email || u).filter(Boolean)

                if (!active) return
                setEmailOptions(emails)
                const total = data?.total || data?.count || (Array.isArray(items) ? items.length : 0)
                setEstimatedReach(ta.selected_emails.length > 0 ? ta.selected_emails.length : total)
            } catch (e) {
                setEmailOptions([])
                setEstimatedReach(formData.target_audience.selected_emails.length)
            } finally {
                if (active) setFetchingUsers(false)
            }
        }, 400)
        return () => {
            active = false
            clearTimeout(handler)
        }
    }, [
        formData.target_audience.allCustomers,
        formData.target_audience.gender,
        formData.target_audience.ageRange?.from,
        formData.target_audience.ageRange?.to,
        formData.target_audience.customerType,
        formData.target_audience.bmiOver25,
        formData.target_audience.selected_emails.length,
    ])

    const handleSubmit = async () => {
        if (!validateForm()) return
        setLoading(true)
        try {
            const payload = {
                title: formData.title,
                message: formData.message,
                type: formData.type,
                target_audience: formData.target_audience,
            }

            let response
            if (editMode) {
                response = await api.put(`/v1/admin/edit/notification/${notificationData?.id}`, payload)
            } else {
                response = await api.post("/v1/admin/create-notification", payload)
            }

            if (response.status === 200 || response.status === 201) {
                setSnackbarMessage(
                    editMode
                        ? en_ja["Notification updated successfully"]
                        : en_ja["Notification created successfully"]
                )
                setSnackbarOpen(true)
                setSnackbarType("success")
                if (onSubmit) onSubmit()
                handleClose()
            }
        } catch (error) {
            setSnackbarMessage(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                en_ja["Something went wrong."]
            )
            setSnackbarOpen(true)
            setSnackbarType("error")
        } finally {
            setLoading(false)
        }
    }

    // Disable submit if loading, or in editMode with no changes made
    const isSubmitDisabled = loading || (editMode && !isEditChange)

    return (
        <Dialog open={dialogOpen} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700 }}
            >
                {editMode ? en_ja["Edit Notification"] : en_ja["Add New Notification"]}
                <IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 14 } }}
                >
                    <Tab label={en_ja["1. Content"]} />
                    <Tab label={en_ja["2. Customer"]} />
                </Tabs>

                {tab === 0 && (
                    <Box sx={{ display: "flex", gap: 3 }}>
                        <Box sx={{ width: "60%" }}>
                            <TextField
                                fullWidth
                                label={en_ja["Notification Title"]}
                                variant="outlined"
                                margin="normal"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                error={!!errors.title}
                                helperText={errors.title}
                            />
                            <TextField
                                fullWidth
                                label={en_ja["Message"]}
                                variant="outlined"
                                margin="normal"
                                multiline
                                rows={3}
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                error={!!errors.message}
                                helperText={errors.message}
                            />
                            <TextField
                                select
                                fullWidth
                                label={en_ja["Type"]}
                                variant="outlined"
                                margin="normal"
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                error={!!errors.type}
                                helperText={errors.type}
                            >
                                <MenuItem value="Promotional">{en_ja["Promotional"]}</MenuItem>
                                <MenuItem value="Transactional">{en_ja["Transactional"]}</MenuItem>
                                <MenuItem value="Newsletter">{en_ja["Newsletter"]}</MenuItem>
                            </TextField>
                        </Box>

                        <Paper variant="outlined" sx={{ flex: 1, p: 2, borderRadius: 2, backgroundColor: "#fafafa" }}>
                            <Typography fontWeight={600} mb={1}>
                                {en_ja["Preview"]}
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1.5,
                                    p: 2,
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 2,
                                    maxHeight: 200,
                                    overflowY: "auto",
                                }}
                            >
                                <CampaignIcon color="error" sx={{ mt: 0.5 }} />
                                <Box>
                                    <Typography fontWeight={600}>{en_ja["Shop Japan"]}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                                        {formData.message || en_ja["Your notification message will appear here..."]}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                )}

                {tab === 1 && (
                    <Box>
                        <Typography fontWeight={700} mb={2}>
                            {en_ja["Customer"]}
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.target_audience.allCustomers}
                                            onChange={(e) =>
                                                handleAudienceChange("allCustomers", e.target.checked)
                                            }
                                        />
                                    }
                                    label={`${en_ja["All Customers"]} (${estimatedReach || 0})`}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!(formData.target_audience.ageRange?.from || formData.target_audience.ageRange?.to)}
                                            disabled={formData.target_audience.allCustomers}
                                            onChange={() =>
                                                handleAudienceChange("ageRange", { from: "", to: "" })
                                            }
                                        />
                                    }
                                    label={en_ja["Age"]}
                                />
                                <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label={en_ja["From"]}
                                        disabled={formData.target_audience.allCustomers}
                                        value={formData.target_audience.ageRange?.from || ""}
                                        onChange={(e) =>
                                            handleAudienceChange("ageRange", {
                                                ...(formData.target_audience.ageRange || {}),
                                                from: e.target.value,
                                            })
                                        }
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        label={en_ja["To"]}
                                        disabled={formData.target_audience.allCustomers}
                                        value={formData.target_audience.ageRange?.to || ""}
                                        onChange={(e) =>
                                            handleAudienceChange("ageRange", {
                                                ...(formData.target_audience.ageRange || {}),
                                                to: e.target.value,
                                            })
                                        }
                                    />
                                </Box>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 3 }}>
                            <Typography fontWeight={700} mb={1}>
                                {en_ja["Gender"]}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.target_audience.gender === 0}
                                            disabled={formData.target_audience.allCustomers}
                                            onChange={(e) =>
                                                handleAudienceChange("gender", e.target.checked ? 0 : null)
                                            }
                                        />
                                    }
                                    label={en_ja["Male"]}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.target_audience.gender === 1}
                                            disabled={formData.target_audience.allCustomers}
                                            onChange={(e) =>
                                                handleAudienceChange("gender", e.target.checked ? 1 : null)
                                            }
                                        />
                                    }
                                    label={en_ja["Female"]}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ mt: 3 }}>
                            <Typography fontWeight={700} mb={1}>
                                {en_ja["Customer Type"]}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.target_audience.customerType === "health"}
                                            disabled={formData.target_audience.allCustomers}
                                            onChange={(e) =>
                                                handleAudienceChange(
                                                    "customerType",
                                                    e.target.checked ? "health" : null
                                                )
                                            }
                                        />
                                    }
                                    label={en_ja["Health Customer"]}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.target_audience.customerType === "non_health"}
                                            disabled={formData.target_audience.allCustomers}
                                            onChange={(e) =>
                                                handleAudienceChange(
                                                    "customerType",
                                                    e.target.checked ? "non_health" : null
                                                )
                                            }
                                        />
                                    }
                                    label={en_ja["Non-Health Customer"]}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.target_audience.bmiOver25}
                                        disabled={formData.target_audience.allCustomers}
                                        onChange={(e) =>
                                            handleAudienceChange("bmiOver25", e.target.checked)
                                        }
                                    />
                                }
                                label="BMI25以上のお客様"
                            />
                        </Box>

                        <Box sx={{ mt: 3 }}>
                            <Autocomplete
                                loading={fetchingUsers}
                                loadingText={en_ja["Loading..."]}
                                multiple
                                disableCloseOnSelect
                                options={[en_ja["Select All"], ...emailOptions]}
                                value={formData.target_audience.selected_emails}
                                onChange={(_, value) => {
                                    if (value.includes(en_ja["Select All"])) {
                                        handleSelectAll()
                                    } else {
                                        handleAudienceChange("selected_emails", value)
                                        setErrors((prev) => ({ ...prev, selected_emails: "" }))
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={en_ja["Select Customer by Email"]}
                                        placeholder={en_ja["Search by Customer Email"]}
                                        error={!!errors.selected_emails}
                                    />
                                )}
                                renderTags={(value, getTagProps) => {
                                    const display = showAll ? value : value.slice(0, 5)
                                    const remaining = value.length - display.length
                                    return (
                                        <>
                                            {display.map((option, index) => (
                                                <Chip
                                                    variant="outlined"
                                                    label={option}
                                                    {...getTagProps({ index })}
                                                    key={option}
                                                />
                                            ))}
                                            {remaining > 0 && !showAll && (
                                                <Button
                                                    size="small"
                                                    onClick={() => setShowAll(true)}
                                                    sx={{ ml: 0.5 }}
                                                >
                                                    {en_ja["View All"]} ({remaining} {en_ja["more"]})
                                                </Button>
                                            )}
                                        </>
                                    )
                                }}
                            />
                            {errors.selected_emails && (
                                <FormHelperText error>{errors.selected_emails}</FormHelperText>
                            )}
                        </Box>

                        <Paper
                            sx={{
                                mt: 3,
                                p: 2,
                                border: "1px solid #f5c2c7",
                                borderRadius: 2,
                                backgroundColor: "#f8d7da",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <GroupIcon color="error" />
                                <Typography variant="body2" fontWeight={600}>
                                    {en_ja["Estimated Reach"]}
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {en_ja["This notification will be sent to approximately"]}{" "}
                                {estimatedReach}{" "}
                                {en_ja["Customers"]}.
                            </Typography>
                        </Paper>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button onClick={handleClose} variant="outlined">
                    {en_ja["Cancel"]}
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    loading={loading}
                    variant="contained"
                    disabled={isSubmitDisabled}
                    sx={{ backgroundColor: "rgba(220, 53, 69, 1)" }}
                >
                    {editMode ? en_ja["Update Notification"] : en_ja["Create Notification"]}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

export default AddNotification